import { useState } from 'react';
import { colour, radius, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, Status, Tag, TextInput, Thumb, Tile, Tiles } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading, MachineOfflineBanner, OfflineBanner } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { api, query } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 9: every run ever made, newest and most urgent first.
 *
 * The flag that matters is the quiet one: a run queued for a machine that is
 * off is not failed, not running, and not going to happen. The server works
 * that out — the row carries `waiting_on_an_offline_machine` — because only the
 * server knows when each machine last said hello.
 *
 * Retry makes a new run and keeps the old (rule 10), which is why "Retry all
 * failed" goes through the launcher one at a time rather than being a bulk
 * update.
 */

interface Run {
  _id: string;
  process: string;
  step_key?: string;
  state: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  owner: string;
  requested_by: string;
  reason?: string;
  executor?: { host: string };
  claimed_by?: string;
  queued_at?: string;
  waiting_on_an_offline_machine: boolean;
  progress: number | null;
  started_by_maestro: boolean;
  thumbnail: string | null;
  says: string;
}

interface Screen {
  runs: Run[];
  counts: Record<string, number>;
  total: number;
  choices: { processes: string[]; owners: string[]; machines: string[] };
}

interface ToolRun {
  position: number;
  tool: string;
  tool_version?: string;
  command: string;
  machine: string;
  blocking: boolean;
  state: 'running' | 'done' | 'failed' | 'waiting' | 'skipped';
  exit_code?: number;
  started_at?: string;
  finished_at?: string;
  log_tail?: string;
  log_uri?: string;
  reason?: string;
}

interface RunDetail {
  run: Run;
  command: string | null;
  why_no_command: string | null;
  auto: { used: number; cap: number };
  tool_runs: ToolRun[];
  chain_says: string | null;
  history: { _id: string; state: string; queued_at?: string }[];
  log_uri: string | null;
}

interface Machine {
  host: string;
  online: boolean;
  retired?: boolean;
  queued: number;
  running: number;
  says: string;
}

const STATES = ['failed', 'running', 'queued', 'done', 'cancelled'] as const;

export function Runs({ projectId }: { projectId: string }) {
  const can = useCan();
  const [state_, setState] = useState<string>('');
  const [q, setQ] = useState('');
  const [process, setProcess] = useState('');
  const [startedBy, setStartedBy] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'retry' | 'cancel' | null>(null);
  const [moving, setMoving] = useState<{ from: string } | null>(null);
  const [detail, setDetail] = useState<
    { loading: boolean; runId: string; data?: RunDetail } | null
  >(null);
  const [moveTo, setMoveTo] = useState('');

  const { state, reload } = useLoad<Screen>(
    () =>
      api.get(
        `/api/runs/screen${query({ project_id: projectId, state: state_, q, process, started_by: startedBy })}`
      ),
    [projectId, state_, q, process, startedBy]
  );
  const { state: machineState, reload: reloadMachines } = useLoad<{ machines: Machine[] }>(
    () => api.get('/api/machines'),
    []
  );

  const screen = state.data;
  const machines = machineState.data?.machines ?? [];
  const stuck = machines.filter((m) => !m.online && !m.retired && m.queued + m.running > 0);

  const act = async (what: 'retry' | 'cancel'): Promise<void> => {
    try {
      const body = { project_id: projectId };
      const result =
        what === 'retry'
          ? await api.post<{ counts: { launched: number; skipped: number } }>('/api/runs/retry-failed', body)
          : await api.post<{ counts: { cancelled: number } }>('/api/runs/cancel-waiting', body);
      setConfirm(null);
      setToast(
        'launched' in result.counts
          ? `Retry queued for ${result.counts.launched} runs.${result.counts.skipped ? ` ${result.counts.skipped} could not be re-run.` : ''}`
          : `Cancelled ${(result.counts as { cancelled: number }).cancelled} waiting runs. Anything already running was left alone.`
      );
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That did not work.');
    }
  };

  const openDetail = async (runId: string): Promise<void> => {
    setDetail({ loading: true, runId });
    try {
      setDetail({ loading: false, runId, data: await api.get<RunDetail>(`/api/runs/${runId}/detail`) });
    } catch (err) {
      setDetail(null);
      setToast(err instanceof Error ? err.message : 'That could not be opened.');
    }
  };

  const retryOne = async (runId: string): Promise<void> => {
    try {
      await api.post(`/api/runs/${runId}/retry`);
      setToast('A new run was made. The old one is still there, with its reason.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That could not be re-run.');
    }
  };

  const cancelOne = async (runId: string): Promise<void> => {
    try {
      await api.post(`/api/runs/${runId}/cancel`, {});
      setToast('Cancelled.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That could not be cancelled.');
    }
  };

  const moveWork = async (): Promise<void> => {
    if (!moving || !moveTo) return;
    try {
      const result = await api.post<{ says: string }>('/api/machines/move-queued', {
        from: moving.from,
        to: moveTo
      });
      setMoving(null);
      setToast(result.says);
      reload();
      reloadMachines();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Those could not be moved.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · RUNS AND QUEUE"
      title="Runs"
      description="Every run ever made. Re-running makes a new one and keeps the old, with its reason."
      action={
        can.launch ? (
          <>
            <Button onClick={() => setConfirm('retry')} disabled={(screen?.counts.failed ?? 0) === 0}>
              Retry all failed
            </Button>
            <Button onClick={() => setConfirm('cancel')} disabled={(screen?.counts.queued ?? 0) === 0}>
              Cancel all waiting
            </Button>
          </>
        ) : undefined
      }
    >
      {state.status === 'offline' && <OfflineBanner since={state.since} onRetry={reload} />}

      {stuck.map((machine) => (
        <MachineOfflineBanner
          key={machine.host}
          host={machine.host}
          waiting={machine.queued + machine.running}
          {...(can.launch ? { onMove: () => setMoving({ from: machine.host }) } : {})}
        />
      ))}

      {screen && (
        <Tiles>
          {STATES.map((key) => (
            <Tile
              key={key}
              count={screen.counts[key] ?? 0}
              label={key === 'done' ? 'Finished' : key.replace(/^./, (c) => c.toUpperCase())}
              on={state_ === key}
              onClick={() => setState(state_ === key ? '' : key)}
            />
          ))}
        </Tiles>
      )}

      <Card>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
            gap: space.control
          }}
        >
          <Field label="Search by VC, sequence or run number" grow>
            <TextInput value={q} onChange={setQ} placeholder="cam0003, seq_010…" />
          </Field>
          <Field label="Process">
            <Select
              value={process}
              onChange={setProcess}
              options={[
                { value: '', label: 'Any' },
                ...(screen?.choices.processes ?? []).map((p) => ({ value: p, label: p }))
              ]}
            />
          </Field>
          <Field label="Started by">
            <Select
              value={startedBy}
              onChange={setStartedBy}
              options={[
                { value: '', label: 'Anybody' },
                { value: 'person', label: 'A person' },
                { value: 'maestro', label: 'Maestro, automatically' }
              ]}
            />
          </Field>
        </div>
      </Card>

      {state.status === 'loading' && !screen && <Loading says="Loading runs…" rows={6} />}
      {state.status === 'failed' && !screen && (
        <CouldNotLoad what="the runs" error={state.error} onRetry={reload} />
      )}
      {screen && screen.runs.length === 0 && (
        <Empty title="No runs match" says="Nothing here yet, or the filters are too narrow." />
      )}

      {(screen?.runs ?? []).map((run) => (
        <Card key={run._id}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'flex-start' }}>
            <Thumb url={run.thumbnail} alt={`${run.process} input`} size={80} />
            <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
                <span style={text.bodyStrong}>{run.step_key ?? run.process}</span>
                <Status value={run.state} />
                {run.waiting_on_an_offline_machine && <Status value="stale" detail="machine is off" />}
                {run.started_by_maestro && <Tag>started by Maestro</Tag>}
              </span>
              <span style={{ ...text.small, color: colour.inkSoft }}>{run.says}</span>
              <span style={{ ...text.note, color: colour.inkMuted }}>
                owner {run.owner} · asked by {run.requested_by}
                {run.executor?.host ? ` · ${run.executor.host}` : ''}
                {run.queued_at ? ` · ${new Date(run.queued_at).toLocaleString()}` : ''}
              </span>
              {run.progress != null && (
                <div
                  role="img"
                  aria-label={`about ${Math.round(run.progress * 100)}% of the usual time`}
                  style={{ height: 8, background: colour.surfaceSunk, borderRadius: radius.pill, overflow: 'hidden' }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: `${Math.round(run.progress * 100)}%`,
                      height: '100%',
                      background: colour.accent
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: space.tight }}>
              <Button onClick={() => void openDetail(run._id)}>What it ran</Button>
            </div>
            {can.launch && (
              <div style={{ display: 'flex', gap: space.tight }}>
                {(run.state === 'failed' || run.state === 'cancelled') && (
                  <Button onClick={() => void retryOne(run._id)}>Retry</Button>
                )}
                {(run.state === 'queued' || run.state === 'running') && (
                  <Button tone="danger" onClick={() => void cancelOne(run._id)}>
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}

      {confirm && (
        <Dialog
          title={confirm === 'retry' ? 'Retry everything that failed' : 'Cancel everything waiting'}
          says={
            confirm === 'retry'
              ? `Each one becomes a new run. The ${screen?.counts.failed ?? 0} failed runs stay exactly as they are, with their reasons.`
              : `The ${screen?.counts.queued ?? 0} queued runs are cancelled. Anything already running is left alone, and the history is kept.`
          }
          confirm={confirm === 'retry' ? 'Retry them' : 'Cancel them'}
          tone={confirm === 'cancel' ? 'danger' : 'primary'}
          onConfirm={() => void act(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}

      {moving && (
        <Dialog
          title={`Move the work off ${moving.from}`}
          says="Only queued runs move. Anything already running is somebody's work in progress on a machine that may simply be quiet, and moving it would run it twice."
          confirm="Move them"
          onConfirm={() => void moveWork()}
          onClose={() => setMoving(null)}
        >
          <Field label="To which machine">
            <Select
              value={moveTo}
              onChange={setMoveTo}
              options={[
                { value: '', label: 'Pick a machine' },
                ...machines
                  .filter((m) => m.host !== moving.from && m.online && !m.retired)
                  .map((m) => ({ value: m.host, label: `${m.host} — ${m.says}` }))
              ]}
            />
          </Field>
        </Dialog>
      )}

      {detail && (
        <Dialog
          title="What this run actually ran"
          says={
            detail.data?.chain_says ??
            'The exact command, as the machine was given it, so debugging does not need access to the workstation.'
          }
          confirm="Close"
          onConfirm={() => setDetail(null)}
          onClose={() => setDetail(null)}
        >
          {detail.loading && <span style={{ ...text.small, color: colour.inkSoft }}>Loading…</span>}

          {(detail.data?.tool_runs ?? []).length === 0 && detail.data?.command && (
            <pre
              style={{
                margin: 0,
                padding: 10,
                borderRadius: radius.control,
                background: colour.surfaceSunk,
                border: `1px solid ${colour.edgeSoft}`,
                ...text.mono,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}
            >
              {detail.data.command}
            </pre>
          )}

          {(detail.data?.tool_runs ?? []).map((tool) => (
            <div
              key={`${tool.position}-${tool.tool}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                paddingTop: space.tight,
                borderTop: `1px solid ${colour.edgeSoft}`
              }}
            >
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
                <span style={text.smallStrong}>
                  {tool.position + 1}. {tool.tool}
                </span>
                <Status value={tool.state} />
                {!tool.blocking && <Tag>hands the work on</Tag>}
                {tool.tool_version && <Tag>v{tool.tool_version}</Tag>}
                <Tag>{tool.machine}</Tag>
                {tool.exit_code != null && <Tag>exit {tool.exit_code}</Tag>}
              </span>
              <pre
                style={{
                  margin: 0,
                  padding: 8,
                  borderRadius: radius.control,
                  background: colour.surfaceSunk,
                  border: `1px solid ${colour.edgeSoft}`,
                  ...text.mono,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {tool.command}
              </pre>
              {tool.reason && (
                <span style={{ ...text.note, color: colour.staleInk }}>{tool.reason}</span>
              )}
              {tool.log_tail && (
                <details>
                  <summary style={{ ...text.note, color: colour.inkSoft, cursor: 'pointer' }}>
                    The end of the log
                  </summary>
                  <pre
                    style={{
                      margin: '4px 0 0',
                      padding: 8,
                      maxHeight: 220,
                      overflow: 'auto',
                      borderRadius: radius.control,
                      background: colour.ink,
                      color: '#e7e4da',
                      ...text.mono,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {tool.log_tail}
                  </pre>
                </details>
              )}
            </div>
          ))}

          {detail.data?.why_no_command && (
            <span style={{ ...text.note, color: colour.staleInk }}>{detail.data.why_no_command}</span>
          )}
          {detail.data && (
            <span style={{ ...text.note, color: colour.inkMuted }}>
              {detail.data.auto.used} of {detail.data.auto.cap} automatic attempts used ·{' '}
              {detail.data.history.length} runs of this step so far
            </span>
          )}
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
